console.log('VIOK PRODUCT-INFO JS GELADEN');

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput =
          this.querySelector('.quantity__input');

        /*
         * VIOK:
         * Wir merken uns die Gallery, sobald sie zum ersten Mal
         * aufgebaut wurde.
         */
        this.viokGalleryInitialized = false;
        this.viokMediaCache = new Map();
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true,
          })
        );

        /*
         * Gallery erst nach dem initialen DOM-Aufbau sichern.
         */
        requestAnimationFrame(() => {
          this.initializeViokGallery();
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm =
          this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add(
                'scroll-trigger--cancel'
              )
            )
        );

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /*
       * ============================================================
       * VIOK GALLERY
       * ============================================================
       */

      getColorMap() {
        return {
          'mattschwarz': 'schwarz_',
          'sand white': 'sand_',
          'sand': 'sand_',
          'silber edelstahl': 'silber_',
          'mattweiß': 'weiss_',
          'mattgrau': 'grau_',
        };
      }

      getImageName(item) {
        if (!item) return '';

        const dataName =
          item.dataset?.variantColor ||
          item.dataset?.imageName;

        if (dataName) {
          return dataName
            .toLowerCase()
            .trim();
        }

        const img =
          item.querySelector?.('img');

        if (!img) return '';

        if (img.alt) {
          return img.alt
            .toLowerCase()
            .trim();
        }

        const src =
          img.currentSrc ||
          img.src ||
          img.getAttribute('src') ||
          '';

        const match =
          src.match(
            /\/([^/?#]+)\.(?:png|jpg|jpeg|webp|avif)(?:[?#]|$)/i
          );

        return match
          ? match[1].toLowerCase().trim()
          : '';
      }

      getImagePrefixFromName(name) {
        const clean =
          (name || '')
            .toLowerCase()
            .trim();

        const colorMap =
          this.getColorMap();

        for (const prefix of Object.values(colorMap)) {
          if (clean.startsWith(prefix)) {
            return prefix;
          }
        }

        return null;
      }

      findImageEverywhere(imageName) {
        const normalized =
          imageName
            .toLowerCase()
            .trim();

        const images =
          document.querySelectorAll('img');

        for (const img of images) {
          const alt =
            (img.alt || '')
              .toLowerCase()
              .trim();

          if (alt === normalized) {
            return img;
          }

          const src =
            img.currentSrc ||
            img.src ||
            img.getAttribute('src') ||
            '';

          const match =
            src.match(
              /\/([^/?#]+)\.(?:png|jpg|jpeg|webp|avif)(?:[?#]|$)/i
            );

          if (
            match &&
            match[1].toLowerCase() === normalized
          ) {
            return img;
          }
        }

        return null;
      }

      createViokGallerySlide(
        imageName,
        sourceImg,
        templateSlide
      ) {
        if (!sourceImg) return null;

        let slide;

        if (templateSlide) {
          slide =
            templateSlide.cloneNode(true);
        } else {
          slide =
            document.createElement('li');

          slide.className =
            'product__media-item grid__item slider__slide';
        }

        const img =
          slide.querySelector('img');

        if (!img) return null;

        const src =
          sourceImg.currentSrc ||
          sourceImg.src ||
          sourceImg.getAttribute('src');

        const srcset =
          sourceImg.getAttribute('srcset');

        if (src) {
          img.setAttribute(
            'src',
            src
          );
        }

        if (srcset) {
          img.setAttribute(
            'srcset',
            srcset
          );
        }

        img.alt =
          imageName;

        [
          'data-src',
          'data-srcset',
          'loading',
          'width',
          'height'
        ].forEach((attribute) => {
          const value =
            sourceImg.getAttribute(
              attribute
            );

          if (value !== null) {
            img.setAttribute(
              attribute,
              value
            );
          }
        });

        const safeName =
          imageName.replace(
            /[^a-z0-9äöüß_-]/gi,
            '-'
          );

        const mediaId =
          `viok-${safeName}`;

        slide.dataset.mediaId =
          mediaId;

        slide.dataset.variantColor =
          imageName;

        slide.dataset.viokGenerated =
          'true';

        if (slide.id) {
          slide.id =
            `${slide.id}-viok-${safeName}`;
        }

        return slide;
      }

      cacheExistingGalleryMedia() {
        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (!mediaGallery) return;

        const items =
          mediaGallery.querySelectorAll(
            'li[data-media-id]'
          );

        items.forEach((item) => {
          const name =
            this.getImageName(item);

          if (!name) return;

          const prefix =
            this.getImagePrefixFromName(name);

          if (!prefix) return;

          if (
            !this.viokMediaCache.has(name)
          ) {
            this.viokMediaCache.set(
              name,
              item
            );
          }
        });
      }

      restoreMissingViokImages() {
        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (!mediaGallery) return;

        const slider =
          mediaGallery.querySelector(
            '.slider'
          );

        if (!slider) return;

        this.cacheExistingGalleryMedia();

        const colorMap =
          this.getColorMap();

        Object.values(colorMap)
          .filter(
            (value, index, array) =>
              array.indexOf(value) === index
          )
          .forEach((prefix) => {
            for (
              let number = 1;
              number <= 8;
              number++
            ) {
              const imageName =
                `${prefix}${number}`;

              const existing =
                Array.from(
                  mediaGallery.querySelectorAll(
                    'li[data-media-id]'
                  )
                ).find((item) =>
                  this.getImageName(item) ===
                  imageName
                );

              if (existing) {
                this.viokMediaCache.set(
                  imageName,
                  existing
                );

                continue;
              }

              const sourceImg =
                this.findImageEverywhere(
                  imageName
                );

              if (!sourceImg) {
                console.log(
                  'VIOK: Bild nicht gefunden:',
                  imageName
                );

                continue;
              }

              const template =
                mediaGallery.querySelector(
                  'li[data-media-id]'
                );

              const newSlide =
                this.createViokGallerySlide(
                  imageName,
                  sourceImg,
                  template
                );

              if (!newSlide) {
                console.log(
                  'VIOK: Slide konnte nicht erzeugt werden:',
                  imageName
                );

                continue;
              }

              slider.appendChild(
                newSlide
              );

              this.viokMediaCache.set(
                imageName,
                newSlide
              );

              console.log(
                'VIOK: Gallery-Slide wiederhergestellt:',
                imageName
              );
            }
          });

        this.viokGalleryInitialized =
          true;
      }

      initializeViokGallery() {
        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (!mediaGallery) {
          console.log(
            'VIOK: Keine media-gallery gefunden'
          );

          return;
        }

        this.restoreMissingViokImages();
      }

      filterVariantMedia(variant) {
        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (
          !mediaGallery ||
          !variant
        ) {
          return;
        }

        this.restoreMissingViokImages();

        const selectedColor =
          (variant.option1 || '')
            .toLowerCase()
            .trim();

        const colorMap =
          this.getColorMap();

        const colorPrefix =
          colorMap[selectedColor];

        if (!colorPrefix) {
          console.log(
            'VIOK: Keine Farbzuordnung für',
            selectedColor
          );

          return;
        }

        const items =
          mediaGallery.querySelectorAll(
            'li[data-media-id]'
          );

        let firstColorOneItem =
          null;

        items.forEach((item) => {
          const imageName =
            this.getImageName(item);

          const shouldShow =
            imageName.startsWith(
              colorPrefix
            );

          item.classList.toggle(
            'variant-media-hidden',
            !shouldShow
          );

          if (
            shouldShow &&
            imageName ===
              `${colorPrefix}1`
          ) {
            firstColorOneItem =
              item;
          }
        });

        if (!firstColorOneItem) {
          console.log(
            'VIOK: Kein *_1 gefunden für:',
            selectedColor,
            colorPrefix
          );

          this.restoreMissingViokImages();

          firstColorOneItem =
            Array.from(
              mediaGallery.querySelectorAll(
                'li[data-media-id]'
              )
            ).find((item) =>
              this.getImageName(item) ===
              `${colorPrefix}1`
            );

          if (!firstColorOneItem) {
            return;
          }
        }

        const firstMediaId =
          firstColorOneItem.dataset.mediaId;

        console.log(
          'VIOK: Starte Farbe bei _1:',
          selectedColor,
          '→',
          this.getImageName(
            firstColorOneItem
          ),
          '→',
          firstMediaId
        );

        if (
          typeof mediaGallery.setActiveMedia ===
          'function'
        ) {
          try {
            mediaGallery.setActiveMedia(
              firstMediaId,
              true
            );
          } catch (error) {
            console.log(
              'VIOK: setActiveMedia konnte nicht verwendet werden',
              error
            );
          }
        }

        const resetSlider = () => {
          const slider =
            mediaGallery.querySelector(
              '.slider'
            );

          if (!slider) return;

          const target =
            mediaGallery.querySelector(
              `li[data-media-id="${CSS.escape(
                firstMediaId
              )}"]`
            );

          if (!target) return;

          mediaGallery
            .querySelectorAll(
              'li[data-media-id]'
            )
            .forEach((item) => {
              const isActive =
                item === target;

              item.classList.toggle(
                'is-active',
                isActive
              );
            });

          try {
            target.scrollIntoView({
              behavior: 'auto',
              block: 'nearest',
              inline: 'start',
            });
          } catch (error) {}

          const targetLeft =
            target.offsetLeft;

          if (
            Number.isFinite(targetLeft)
          ) {
            slider.scrollLeft =
              targetLeft;

            slider.scrollTo({
              left: targetLeft,
              behavior: 'auto',
            });
          }

          if (
            typeof slider.scrollTo ===
            'function'
          ) {
            slider.scrollTo({
              left: targetLeft,
              behavior: 'auto',
            });
          }
        };

        requestAnimationFrame(() => {
          resetSlider();

          requestAnimationFrame(() => {
            resetSlider();

            setTimeout(() => {
              resetSlider();
            }, 50);

            setTimeout(() => {
              resetSlider();
            }, 150);

            setTimeout(() => {
              resetSlider();
            }, 300);
          });
        });
      }

      /*
       * ============================================================
       * VARIANT CHANGE
       * ============================================================
       */

      handleOptionValueChange({
        data: {
          event,
          target,
          selectedOptionValues,
        },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl =
          productUrl;

        const shouldSwapProduct =
          this.dataset.url !==
          productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct;

        this.renderProductInfo({
          requestUrl:
            this.buildRequestUrlWithParams(
              productUrl,
              selectedOptionValues,
              shouldFetchFullPage
            ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(
                productUrl
              ),
        });
      }

      resetProductFormState() {
        const productForm =
          this.productForm;

        productForm?.toggleSubmitButton(
          true
        );

        productForm?.handleErrorMessage();
      }

      handleSwapProduct(
        productUrl,
        updateFullPage
      ) {
        return (html) => {
          this.productModal?.remove();

          const selector =
            updateFullPage
              ? "product-info[id^='MainProduct']"
              : 'product-info';

          const variant =
            this.getSelectedVariant(
              html.querySelector(
                selector
              )
            );

          this.updateURL(
            productUrl,
            variant?.id
          );

          if (updateFullPage) {
            document.querySelector(
              'head title'
            ).innerHTML =
              html.querySelector(
                'head title'
              ).innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector(
                'product-info'
              ),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({
        requestUrl,
        targetId,
        callback,
      }) {
        this.abortController?.abort();

        this.abortController =
          new AbortController();

        fetch(requestUrl, {
          signal:
            this.abortController.signal,
        })
          .then((response) =>
            response.text()
          )
          .then((responseText) => {
            this.pendingRequestUrl =
              null;

            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            callback(html);
          })
          .then(() => {
            document
              .querySelector(
                `#${targetId}`
              )
              ?.focus();
          })
          .catch((error) => {
            if (
              error.name ===
              'AbortError'
            ) {
              console.log(
                'Fetch aborted by user'
              );
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(
        productInfoNode
      ) {
        const selectedVariant =
          productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          )?.innerHTML;

        if (!selectedVariant) {
          console.log(
            'VIOK INVENTORY TEST: kein Variant-JSON gefunden'
          );

          return null;
        }

        const variant =
          JSON.parse(selectedVariant);

        console.log(
          'VIOK INVENTORY TEST – KOMPLETTES VARIANT-JSON:',
          variant
        );

        return variant;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage &&
          params.push(
            `section_id=${this.sectionId}`
          );

        if (optionValues.length) {
          params.push(
            `option_values=${optionValues.join(',')}`
          );
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects =
          html.querySelector(
            'variant-selects'
          );

        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      /*
       * ============================================================
       * INVENTORY
       * ============================================================
       */

updateInventoryDisplay(
  destination,
  variant
) {
  if (
    !destination ||
    !variant
  ) {
    return;
  }

  // Alten Liefertext entfernen
  destination
    .querySelectorAll(
      '.viok-delivery-time'
    )
    .forEach((element) =>
      element.remove()
    );

  const inventoryQuantity =
    Number(
      variant.inventory_quantity
    );

  // Nur bei echtem Lagerbestand Lieferzeit anzeigen
  if (
    variant.inventory_management !==
      'shopify' ||
    !Number.isFinite(
      inventoryQuantity
    ) ||
    inventoryQuantity <= 0
  ) {
    return;
  }

  // Lieferzeit als Text direkt in den Inventory-Text schreiben
  const deliveryText =
    document.createTextNode(
      ' – in 1–3 Werktagen bei dir'
    );

  destination.appendChild(
    deliveryText
  );
}
      handleUpdateProductInfo(
        productUrl
      ) {
        return (html) => {
          const variant =
            this.getSelectedVariant(
              html
            );

          this.pickupAvailability?.update(
            variant
          );

          this.updateOptionValues(
            html
          );

          this.updateURL(
            productUrl,
            variant?.id
          );

          this.updateVariantInputs(
            variant?.id
          );

          if (!variant) {
            this.setUnavailable();
            return;
          }

          /*
           * WICHTIG:
           *
           * updateMedia() darf unsere Gallery nicht mehr
           * anhand der Shopify-Antwort löschen.
           */
          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          /*
           * Erst fehlende Bilder sichern,
           * dann Farbe filtern und auf _1 springen.
           */
          this.restoreMissingViokImages();

          this.filterVariantMedia(
            variant
          );

          console.log(
            'VIOK INVENTORY HTML:',
            html.getElementById(
              `Inventory-${this.sectionId}`
            )?.outerHTML
          );

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source =
              html.getElementById(
                `${id}-${this.sectionId}`
              );

            const destination =
              this.querySelector(
                `#${id}-${this.dataset.section}`
              );

            if (
              source &&
              destination
            ) {
              destination.innerHTML =
                source.innerHTML;

              destination.classList.toggle(
                'hidden',
                shouldHide(source)
              );

              /*
               * Nur beim Inventory:
               *
               * Shopify hat gerade den aktuellen
               * FFN/Shopify-Bestand gerendert.
               *
               * Wir verändern diesen Bestand nicht.
               * Wir hängen lediglich die Lieferzeit
               * an den bereits vorhandenen Text.
               */
              if (
                id === 'Inventory'
              ) {
                this.updateInventoryDisplay(
                  destination,
                  variant
                );
              }
            }
          };

          updateSourceFromDestination(
            'price'
          );

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains(
                'hidden'
              )
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText === ''
          );

          updateSourceFromDestination(
            'Volume'
          );

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains(
                'hidden'
              )
          );

          this.updateQuantityRules(
            this.sectionId,
            html
          );

          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove(
            'hidden'
          );

          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove(
            'hidden'
          );

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(
                `ProductSubmitButton-${this.sectionId}`
              )
              ?.hasAttribute(
                'disabled'
              ) ?? true,
            window.variantStrings.soldOut
          );

          publish(
            PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId:
                  this.sectionId,
                html,
                variant,
              },
            }
          );
        };
      }

      /*
       * ============================================================
       * WICHTIG:
       * updateMedia() LÖSCHT KEINE GALLERY-SLIDES MEHR.
       * ============================================================
       */

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        const mediaGallery =
          this.querySelector(
            'media-gallery'
          );

        if (!mediaGallery) return;

        if (
          this.hasAttribute(
            'data-zoom-on-hover'
          ) &&
          typeof enableZoomOnHover ===
            'function'
        ) {
          enableZoomOnHover(2);
        }

        /*
         * Modal-Inhalt weiterhin aktualisieren.
         */
        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          html.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }

        /*
         * GANZ WICHTIG:
         *
         * Keine der alten Dawn-Zeilen mehr verwenden, die
         * mediaGallerySourceItems entfernen.
         *
         * Unsere Gallery wird von VIOK kontrolliert.
         */

        requestAnimationFrame(() => {
          this.restoreMissingViokImages();
        });
      }

      /*
       * ============================================================
       * RESTLICHE DAWN-FUNKTIONEN
       * ============================================================
       */

      updateVariantInputs(
        variantId
      ) {
        this.querySelectorAll(
          'input.product-variant-id[name="id"], input[name="id"]'
        ).forEach((input) => {
          input.value =
            variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        });
      }

      updateURL(
        url,
        variantId
      ) {
        this.href = variantId
          ? `${url}?variant=${variantId}`
          : url;

        if (
          this.dataset.updateUrl ===
            'false' ||
          !variantId
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}?variant=${variantId}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const price =
          this.querySelector(
            '.price'
          );

        if (price) {
          price.classList.add(
            'visibility-hidden'
          );
        }
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (!this.quantityInput) return;

        const quantityRules =
          html.querySelector(
            `#Quantity-Rules-${sectionId}`
          );

        if (!quantityRules) return;

        const destination =
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          );

        if (destination) {
          destination.innerHTML =
            quantityRules.innerHTML;
        }

        this.setQuantityBoundries();
      }

      fetchQuantityRules() {
        if (!this.quantityInput) return;

        const variantId =
          this.productForm?.variantIdInput
            ?.value;

        if (!variantId) return;

        const url =
          `${this.dataset.url}?variant=${variantId}&section_id=${this.sectionId}`;

        fetch(url)
          .then((response) =>
            response.text()
          )
          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            this.updateQuantityRules(
              this.sectionId,
              html
            );
          })
          .catch((error) => {
            console.error(error);
          });
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const cartQuantity =
          parseInt(
            this.quantityInput.dataset
              .cartQuantity,
            10
          ) || 0;

        const min =
          parseInt(
            this.quantityInput.getAttribute(
              'min'
            ),
            10
          ) || 1;

        const maxAttribute =
          this.quantityInput.getAttribute(
            'max'
          );

        const max =
          maxAttribute !== null
            ? parseInt(
                maxAttribute,
                10
              )
            : null;

        if (max !== null) {
          this.quantityInput.value =
            Math.min(
              Math.max(
                parseInt(
                  this.quantityInput.value,
                  10
                ) || min,
                min
              ),
              max
            );
        } else {
          this.quantityInput.value =
            Math.max(
              parseInt(
                this.quantityInput.value,
                10
              ) || min,
              min
            );
        }

        this.quantityInput.dataset.cartQuantity =
          cartQuantity;
      }

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get productModal() {
        return document.querySelector(
          'product-modal'
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }

      get sectionId() {
        return this.dataset.section;
      }
    }
  );
}